// ✅ Importaciones de React y utilidades necesarias
import { useState, useEffect } from "react";
import { ButtonSiguiente, ButtonPopup, Input } from "../../ui";
import { jwtDecode } from 'jwt-decode';
import PopupGenerico from "../../popup/PopupGenerico";
import Alerta from "../../alert/Alerta";
import { TokenPayload, Usuario } from "@/types";
import { isValidContrasena, isValidEmail } from "../../validation/validations";

// API dinámica
import { safeFetch } from "@/api/safeFetch";
import { Role } from "@/utils/rbac";

const EditaUsuario: React.FC = () => {

  // 🧱 Estado del usuario actual (PARCIAL para evitar errores)
  const [usuario, setUsuario] = useState<Partial<Usuario>>({});

  // 📦 Guardamos la versión original para comparar
  const [originalUsuario, setOriginalUsuario] = useState<Partial<Usuario>>({});
  const [isModified, setIsModified] = useState(false);

  // 👤 Nombre de usuario y rol
  const [userName, setUserName] = useState<string | null>(null);
  const [, setRoles] = useState<Role[] | null>(null);


  // 🔐 Modal de contraseña
  const [showModal, setShowModal] = useState(false);
  const [enteredCurrentPassword, setEnteredCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  // ⚠️ Alertas
  const [showAlert, setShowAlert] = useState(false);
  const [alertMensaje, setAlertMensaje] = useState('');
  const [, setAlertTipo] = useState<'CambiosRealizados' | string>('CambiosRealizados');

  const mostrarAlerta = (mensaje: string, tipo: string = 'CambiosRealizados') => {
    setAlertMensaje(mensaje);
    setAlertTipo(tipo);
    setShowAlert(true);
  };

  // 🔓 Decodificar token
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;

    try {
      const decoded: TokenPayload = jwtDecode<TokenPayload>(token);
      setUserName(decoded.username || null);
      setRoles(decoded.roles || null);
    } catch (err) {
      console.error("Error decodificando token:", err);
    }
  }, []);

  // 🌐 Obtener datos del usuario
  useEffect(() => {
    if (!userName) return;

    const fetchUsuario = async () => {
      const token = localStorage.getItem("token");
      if (!token) {
        mostrarAlerta("Token no disponible.", "noFirma");
        return;
      }

      try {
        const res = await safeFetch(`/api/datosUsuario/${userName}`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        if (!res.ok) throw new Error("Error al obtener datos del usuario");

        const data: Usuario = await res.json();

        setUsuario(data);
        setOriginalUsuario(data);

      } catch (err: any) {
        mostrarAlerta(err.message, "noFirma");
      }
    };

    fetchUsuario();
  }, [userName]);

  // 🔍 Detectar cambios
  useEffect(() => {
    const changed = Object.keys(usuario).some(
      key => usuario[key as keyof Usuario] !== originalUsuario[key as keyof Usuario]
    );
    setIsModified(changed);
  }, [usuario, originalUsuario]);

  // ⏱️ Ocultar alertas
  useEffect(() => {
    if (!showAlert) return;
    const timer = setTimeout(() => setShowAlert(false), 4000);
    return () => clearTimeout(timer);
  }, [showAlert]);

  // 🪟 Modal
  const handleOpenModal = () => setShowModal(true);
  const handleCloseModal = () => {
    setShowModal(false);
    setEnteredCurrentPassword("");
    setNewPassword("");
    setConfirmPassword("");
  };

  // 💾 Guardar cambios
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!usuario.email?.trim()) return mostrarAlerta("El correo electrónico no puede estar vacío.", "email");
    if (!isValidEmail(usuario.email)) return mostrarAlerta("Formato de correo inválido.", "email");
    if (!isModified) return mostrarAlerta("No se ha modificado ningún dato.", "CambiosRealizados");
    if (!userName) return mostrarAlerta("Usuario no disponible. Recarga la página.", "noFirma");

    const token = localStorage.getItem("token");
    if (!token) return mostrarAlerta("No estás autenticado. Inicia sesión de nuevo.", "noFirma");

    try {
      const res = await safeFetch(`/api/editarUsuario/${userName}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(usuario)
      });

      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(errorData.error || errorData.message || `Error ${res.status}`);
      }

      const data = await res.json();

      if (data.newToken) {
        localStorage.setItem("token", data.newToken);
        window.dispatchEvent(new CustomEvent("tokenChanged"));

        try {
          const decoded: TokenPayload = jwtDecode<TokenPayload>(data.newToken);
          setUserName(decoded.username || null);
          setRoles(decoded.roles || null);
        } catch (err) {
          console.error("Error decodificando token nuevo:", err);
        }
      }

      setUserName(usuario.nombre_usuario || userName);
      setOriginalUsuario(data.usuario || usuario);
      setUsuario(data.usuario || usuario);
      setIsModified(false);

      mostrarAlerta("Datos modificados correctamente.", "CambiosRealizados");

    } catch (err: any) {
      mostrarAlerta(err.message || "Error desconocido.", "noFirma");
    }
  };

  // 🔒 Cambiar contraseña
  const handlePasswordChange = async () => {
    const passwordError = isValidContrasena(newPassword);
    if (passwordError) return mostrarAlerta(passwordError, "noFirma");

    if (newPassword !== confirmPassword)
      return mostrarAlerta("Las contraseñas no coinciden", "noFirma");

    const token = localStorage.getItem("token");
    if (!token) return mostrarAlerta("Token no disponible", "noFirma");

    try {
      const res = await safeFetch(`/api/editarContraUsuario/${userName}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ contra: enteredCurrentPassword, newcontra: newPassword })
      });

      if (!res.ok) throw new Error("Error al cambiar la contraseña");

      mostrarAlerta("Contraseña cambiada correctamente", "CambiosRealizados");
      handleCloseModal();

    } catch (err: any) {
      mostrarAlerta(err.message, "noFirma");
    }
  };

const esUsuarioNormal = !usuario.roles?.some(r =>
  ["profesional", "afiliado", "admin", "superadmin"].includes(r)
);


  const tipoUsuario = usuario.roles?.includes("superadmin")
    ? "Superadmin"
    : usuario.roles?.includes("admin")
      ? "Admin"
      : usuario.roles?.includes("profesional")
        ? "Profesional"
        : usuario.roles?.includes("afiliado")
          ? "Afiliado"
          : "Usuario";


  // 🧩 Renderizado del formulario
  return (
    <div className="flex flex-col rounded-2xl px-4 md:mt-20 lg:mt-0 xs:w-screen lg:w-full">
      {showAlert && (
        <Alerta
          mensaje={alertMensaje}
          type={'CambiosRealizados'}
          onClose={() => setShowAlert(false)}
        />
      )}

      <div className="p-6 rounded-2xl shadow-2xl border border-black border-b-2 border-r-8 mb-2 bg-white">
        <form onSubmit={handleSubmit} className="md:space-y-6 xs:space-y-0">
          <h1 className="text-center text-3xl font-bold dark:text-black">
            Edita tus datos
          </h1>

          {/* 🔥 GRID RESPONSIVE REAL */}
          <div
            className={`grid gap-6 p-4 justify-items-stretch
            grid-cols-1
            md:grid-cols-2
            ${esUsuarioNormal ? "lg:grid-cols-2" : "lg:grid-cols-3"}
          `}
          >

            {/* 🧩 BLOQUE 1 */}
            <div className="space-y-4 w-full">
              <div>
                <label className="block text-sm font-semibold text-zinc-700 mb-1">
                  Nombre de usuario:
                </label>
                <Input
                  type="text"
                  value={usuario.nombre_usuario}
                  onChange={(e) =>
                    setUsuario({ ...usuario, nombre_usuario: e.target.value })
                  }
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-zinc-700 mb-1">
                  Correo Electrónico:
                </label>
                <Input
                  type="email"
                  value={usuario.email}
                  readOnly
                  disabled
                  className="dark:bg-blue-100 cursor-not-allowed"
                />
              </div>
            </div>

            {/* 🧩 BLOQUE 2 (solo si NO es usuario normal) */}
            {!esUsuarioNormal && (
              <div className="space-y-4 w-full">
                <div>
                  <label className="block text-sm font-semibold text-zinc-700 mb-1">
                    Nombre:
                  </label>
                  <Input
                    type="text"
                    value={usuario.nombre}
                    readOnly
                    disabled
                    className="dark:bg-blue-100 cursor-not-allowed"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-zinc-700 mb-1">
                    Apellidos:
                  </label>
                  <Input
                    type="text"
                    value={usuario.apellidos}
                    readOnly
                    disabled
                    className="dark:bg-blue-100 cursor-not-allowed"
                  />
                </div>
              </div>
            )}

            {/* 🧩 BLOQUE 3 */}
            <div className="space-y-4 w-full">

              <div>
                <label className="block text-sm font-semibold text-zinc-700 mb-1">
                  Contraseña:
                </label>
                <ButtonSiguiente
                  className="w-full"
                  onClick={(e) => {
                    e.preventDefault();
                    handleOpenModal();
                  }}
                  type="button"
                >
                  Cambiar contraseña
                </ButtonSiguiente>
              </div>

              {/* ❌ DNI/NIE SOLO AFILIADO */}
              {usuario.esAfiliado && (
                <div>
                  <label className="block text-sm font-semibold text-zinc-700 mb-1">
                    DNI / NIE:
                  </label>
                  <Input
                    type="text"
                    value={usuario.dni ?? usuario.nie ?? ""}
                    readOnly
                    disabled
                    className="dark:bg-orange-100 cursor-not-allowed"
                  />
                </div>
              )}

              {/* ✅ TIPO USUARIO SIEMPRE */}
              <div>
                <label className="block text-sm font-semibold text-zinc-700 mb-1">
                  Tipo de usuario:
                </label>

                <Input
                  type="text"
                  value={tipoUsuario}
                  readOnly
                  disabled
                  className={`cursor-not-allowed ${tipoUsuario === "Profesional"
                      ? "!bg-blue-100"
                      : tipoUsuario === "Afiliado"
                        ? "!bg-orange-100"
                        : tipoUsuario === "Admin"
                          ? "!bg-purple-100"
                          : tipoUsuario === "Superadmin"
                            ? "!bg-red-100"
                            : "!bg-green-100"
                    }`}
                />

              </div>
            </div>
          </div>

          {/* BOTÓN */}
          <div className="pt-2 xs:pt-0 flex justify-center">
            <ButtonSiguiente type="submit" className="xs:m-4 md:m-0">
              Confirmar datos
            </ButtonSiguiente>
          </div>
        </form>

        {/* 🔐 MODAL */}
        {showModal && (
          <PopupGenerico isOpen={showModal} onClose={handleCloseModal}>
            <div className="space-y-4">
              <h2 className="text-xl font-bold mb-4 text-center">
                Cambiar Contraseña
              </h2>

              <Input
                type="password"
                placeholder="Contraseña actual"
                value={enteredCurrentPassword}
                onChange={(e) => setEnteredCurrentPassword(e.target.value)}
              />

              <Input
                type="password"
                placeholder="Nueva contraseña A-Za-z0-9 & # _"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
              />

              <Input
                type="password"
                placeholder="Confirmar nueva contraseña"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
              />

              <div className="flex justify-center gap-4">
                <ButtonPopup
                  onClick={handleCloseModal}
                  className="hover:border-red-600 hover:text-red-600"
                >
                  Cancelar
                </ButtonPopup>

                <ButtonPopup
                  onClick={handlePasswordChange}
                  className="hover:text-blue-800"
                >
                  Guardar
                </ButtonPopup>
              </div>
            </div>
          </PopupGenerico>
        )}
      </div>
    </div>
  );
};

// 🚀 Exportamos el componente
export default EditaUsuario;
